import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useRef, useEffect, useMemo } from "react";
import * as THREE from "three";

export interface ModelObject {
  id: string;
  name: string;
  mesh: THREE.Mesh;
  color: string;
  texture?: string;
}

interface ModelProps {
  color: string;
  texture?: string;
  selectedObjectId?: string;
  onObjectsExtracted?: (objects: ModelObject[]) => void;
  onObjectColorUpdated?: (objectId: string, color: string) => void;
  onObjectTextureUpdated?: (objectId: string, texture: string) => void;
  applyTextureToSelected?: boolean;
  textureAdjust?: {
    offset: [number, number];
    rotation: number;
    scale: number;
  };
}

export function Model({
  color,
  texture,
  selectedObjectId,
  onObjectsExtracted,
  onObjectColorUpdated,
  onObjectTextureUpdated,
  applyTextureToSelected,
  textureAdjust,
}: ModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const { scene } = useGLTF("/models/skateboard_deck_20260323.glb");
  const currentTextureRef = useRef<THREE.Texture | null>(null);
  const lastColorRef = useRef<string>(color);
  const lastTextureRef = useRef<string | undefined>(undefined);
  const lastAppliedObjectIdRef = useRef<string | undefined>(undefined);

  // 1. 预克隆场景并创建 Mesh 映射表 (ID -> Mesh)
  const { modelScene, meshMap } = useMemo(() => {
    const clone = scene.clone();
    const map = new Map<string, THREE.Mesh>();
    let index = 0;

    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const objectId = `mesh-${index++}`;
        child.material = child.material.clone();
        map.set(objectId, child);
      }
    });
    return { modelScene: clone, meshMap: map };
  }, [scene]);

  // 2. 提取对象信息并通知父组件 (仅在 meshMap 变化时执行)
  useEffect(() => {
    const objects: ModelObject[] = Array.from(meshMap.entries()).map(([id, mesh]) => {
      const colorHex = (mesh.material instanceof THREE.MeshStandardMaterial || mesh.material instanceof THREE.MeshBasicMaterial)
        ? "#" + mesh.material.color.getHexString()
        : "#ffffff";
      return {
        id,
        name: mesh.name || `Object ${id.split("-")[1]}`,
        mesh,
        color: colorHex,
      };
    });

    onObjectsExtracted?.(objects);
  }, [meshMap, onObjectsExtracted]);

  // 3. 处理全局颜色 (当没有选中对象时)
  useEffect(() => {
    // 只有当明确没有选中对象，且模型已经提取完成时才应用全局颜色
    if (!selectedObjectId && meshMap.size > 0) {
      meshMap.forEach((mesh) => {
        if (mesh.material instanceof THREE.MeshStandardMaterial || mesh.material instanceof THREE.MeshBasicMaterial) {
          mesh.material.color.set(color);
        }
      });
    }
  }, [color, selectedObjectId, meshMap]);

  // 4. 处理选中对象的颜色更新
  useEffect(() => {
    if (selectedObjectId) {
      const targetMesh = meshMap.get(selectedObjectId);
      if (targetMesh) {
        if (targetMesh.material instanceof THREE.MeshStandardMaterial || targetMesh.material instanceof THREE.MeshBasicMaterial) {
          targetMesh.material.color.set(color);
        }
        if (onObjectColorUpdated && color !== lastColorRef.current) {
          onObjectColorUpdated(selectedObjectId, color);
          lastColorRef.current = color;
        }
      }
    }
  }, [color, selectedObjectId, meshMap, onObjectColorUpdated]);

  // 5. 处理纹理更新
  useEffect(() => {
    if (selectedObjectId && texture && applyTextureToSelected) {
      // 避免重复应用相同纹理
      if (texture === lastTextureRef.current && selectedObjectId === lastAppliedObjectIdRef.current) {
        return;
      }

      const targetMesh = meshMap.get(selectedObjectId);
      if (targetMesh) {
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(texture, (loadedTexture) => {
          // 配置纹理属性
          loadedTexture.colorSpace = THREE.SRGBColorSpace; // 设置色彩空间为 sRGB
          loadedTexture.wrapS = loadedTexture.wrapT = THREE.RepeatWrapping;
          loadedTexture.flipY = false;

          // 水平反转修正：repeat.x 为负，offset.x 补偿 1
          const s = textureAdjust ? 1 / textureAdjust.scale : 1;
          loadedTexture.repeat.set(-s, s);
          loadedTexture.offset.set((textureAdjust?.offset[0] ?? 0) + 1, textureAdjust?.offset[1] ?? 0);

          if (textureAdjust) {
            loadedTexture.rotation = textureAdjust.rotation;
          }

          if (targetMesh.material instanceof THREE.MeshStandardMaterial) {
            targetMesh.material.map = loadedTexture;
            targetMesh.material.needsUpdate = true;
          }
          currentTextureRef.current = loadedTexture;

          onObjectTextureUpdated?.(selectedObjectId, texture);
        });

        lastTextureRef.current = texture;
        lastAppliedObjectIdRef.current = selectedObjectId;
      }
    }
  }, [texture, selectedObjectId, meshMap, onObjectTextureUpdated, applyTextureToSelected, textureAdjust]);

  // 6. 处理纹理精细调节 (Offset/Rotation/Scale)
  useEffect(() => {
    const tex = currentTextureRef.current;
    if (tex && textureAdjust) {
      const s = 1 / textureAdjust.scale;
      // 保持水平反转修正
      tex.repeat.set(-s, s);
      tex.offset.set(textureAdjust.offset[0] + 1, textureAdjust.offset[1]);
      tex.rotation = textureAdjust.rotation;
      tex.needsUpdate = true;
    }
  }, [textureAdjust]);

  // 7. 自动计算模型居中与缩放
  useEffect(() => {
    if (groupRef.current) {
      const box = new THREE.Box3().setFromObject(modelScene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const scale = 5 / Math.max(size.x, size.y, size.z);

      groupRef.current.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
      groupRef.current.scale.set(scale, scale, scale);

      console.log('modeluseEffect > scale', scale, center)
    }
  }, [modelScene]);

  useFrame(() => {
    if (groupRef.current && !textureAdjust) {
      groupRef.current.rotation.y += 0.005;
    }
  });

  return (
    <group ref={groupRef}>
      {/* 环境光 */}
      <ambientLight intensity={1} />
      {/* 主方向光 */}
      <directionalLight position={[5, 5, 5]} intensity={1.2} castShadow />
      {/* 补光强度 */}
      <directionalLight position={[-5, 3, -5]} intensity={0.6} />
      <primitive object={modelScene} />
    </group>
  );
}
